import { type Scene } from '../scene/Scene';
import { type Unsubscribe } from '../utils/Unsubscribe';
import { DebugDraw } from './DebugDraw';
import { FrameStatistics } from './FrameStatistics';
import { inspectScene } from './SceneInspector';
import { type SceneSnapshot } from './SceneSnapshot';

/** Called at the end of every frame while debugging is on, with the scene the frame ran against. */
export type DebugFrameListener = (scene: Scene) => void;

/**
 * `ctx.debug`: what the engine knows about itself, and nothing about how it is shown.
 *
 * Three things live here, all read-only from the engine's point of view. {@link DebugService.draw}
 * collects this frame's shapes, {@link DebugService.stats} averages the recent frames, and
 * {@link DebugService.inspect} freezes a scene into a snapshot on demand. Painting any of it belongs to
 * a tool, which in this repository is `@kite/editor`; the engine never creates an element.
 *
 * Everything is off until a host turns it on, so a shipping build pays for one boolean test per frame.
 */
export class DebugService {
  /** This frame's debug shapes, in world units. */
  readonly draw = new DebugDraw();
  /** Rolling frame timings and scene counts. */
  readonly stats = new FrameStatistics();

  /**
   * Whether sprite and collider bounds are added to {@link DebugService.draw} each frame.
   *
   * Off even while debugging is on, so attaching a tool never changes what the game looks like. A
   * developer asks for outlines when they want them; the portal binds F2 to this.
   */
  drawBounds = false;

  readonly #frameListeners = new Set<DebugFrameListener>();

  /** Whether debugging runs at all. Turning it either way starts the shapes and averages clean. */
  get enabled(): boolean {
    return this.draw.enabled;
  }

  set enabled(value: boolean) {
    if (value === this.draw.enabled) {
      return;
    }
    this.draw.enabled = value;
    this.draw.clear();
    this.stats.reset();
  }

  /** Freezes a scene into a read-only snapshot. Costs a walk of the whole tree; call it sparingly. */
  inspect(scene: Scene): SceneSnapshot {
    return inspectScene(scene);
  }

  /**
   * Subscribes to the end of each debugged frame and returns the function that cancels it.
   *
   * This is the moment the shape list is complete and not yet cleared, so it is the only point an
   * overlay can read it. Listeners run inside the frame, so what they do belongs in the frame budget.
   */
  addFrameListener(listener: DebugFrameListener): Unsubscribe {
    this.#frameListeners.add(listener);
    return () => {
      this.#frameListeners.delete(listener);
    };
  }

  /**
   * Announces that the frame's debug output is complete.
   *
   * @internal Called by `DebugSystem` in the debug stage; hosts subscribe instead.
   */
  publishFrame(scene: Scene): void {
    for (const listener of [...this.#frameListeners]) {
      listener(scene);
    }
  }
}
